import { action, observable } from "mobx";
import { message } from "antd";
import ApiManager from "../../mixins/ApiManager";
import { ErrorValueType } from "./CreateCmrStore";
import ee from "../../services/EventEmitter";
import { DOWNLOAD_CMR, REFRESH_CMR_TABLE } from "../../constants/events";
import { STATUS_VALIDATION_ERROR } from "../../constants/http";
import { Cmr } from "../../models/Cmr";
import { Payments } from "../../models/Payments";
import parseMoment from "../../helpers/moment";

interface IErrors {
  cmr_place?: ErrorValueType;
  cmr_date?: ErrorValueType;
}

class UpdateCmrStore extends ApiManager {
  @observable
  private _errors: IErrors = {};

  @observable
  cmr: Cmr | null = null;

  get errors(): IErrors {
    return this._errors;
  }

  private isLoading = false;

  @action
  loadCmr(id: unknown): Promise<void> {
    return this.apiManager
      .getCmr(id)
      .then(async (response) => {
        const data = await response.json();

        if (data.id) {
          this.cmr = UpdateCmrStore.modifyCmrData(data);
        }
      })
      .catch(async (response) => {
        const data = await response.json();

        message.error(data.message);
      })
      .finally(() => {
        this.isLoading = false;
      });
  }

  private static modifyCmrData(data: Cmr): Cmr {
    let payments = {};

    if (Array.isArray(data.carriage_payments)) {
      data.carriage_payments.forEach((item: Payments) => {
        payments = {
          ...payments,
          [item.name]: {
            consignee: item.consignee || "",
            currency1: item.currency1 || "",
            currency2: item.currency2 || "",
            consignor: item.consignor || "",
          },
        };
      });
    }

    return {
      ...data,
      cmr_date: parseMoment(data.cmr_date as string, "YYYY-MM-DD"),
      loading_date: parseMoment(data.loading_date as string, "YYYY-MM-DD"),
      arrival_for_loading: parseMoment(
        data.arrival_for_loading as string,
        "YYYY-MM-DD HH:mm:ss"
      ),
      departure: parseMoment(data.departure as string, "YYYY-MM-DD HH:mm:ss"),
      payments,
      department_id: data.department_id ? data.department_id.toString() : null,
    };
  }

  @action
  update(id: unknown, values: Object): Promise<Response> {
    return this.apiManager
      .updateCmr(id, values)
      .then(async (response) => {
        const data = await response.json();

        this._errors = {};

        message.success(data.message);

        ee.emit(REFRESH_CMR_TABLE, null);

        return response;
      })
      .catch(async (response) => {
        const data = await response.json();

        if (response.status === STATUS_VALIDATION_ERROR) {
          this._errors = data.errors;
        }

        message.error(data.message);

        return response;
      });
  }

  @action
  saveAndDownload(id: unknown, params: Object): void {
    this.update(id, params).then(async (response) => {
      if (!response.ok) {
        return;
      }

      ee.emit(DOWNLOAD_CMR, {
        id,
      });
    });
  }

  @action
  copyToNew(id: unknown): Promise<unknown> {
    return this.apiManager
      .copyToNew(id)
      .then(async (response) => {
        const data = await response.json();

        message.success(data.message);
        ee.emit(REFRESH_CMR_TABLE, null);

        return data.entity.id;
      })
      .catch(async (response) => {
        const data = await response.json();
        message.error(data.message);

        return null;
      });
  }

  @action
  resetCmr(): void {
    this.cmr = null;
  }
}

export default UpdateCmrStore;
